import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import {
    ScoringConfigType,
    type IScoringRuleDto,
} from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { isScoringRuleAccountScoped } from "app/pages/sales-intelligence/domain/leads/utils/isScoringRuleAccountScoped";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { DualScoringBoardContainer } from "app/pages/sales-intelligence/modules/lead-scoring-agent/containers/DualScoringBoardContainer";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";

const SELECTION_DISABLED_TOOLTIP_KEY = "si.pages.score_settings.card.selection_disabled.tooltip";

type Props = {
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    isBootstrapping: boolean;
    selectedRuleIds: ReadonlySet<string>;
    defaultTeamIds: readonly number[];
    onToggleRule: (ruleId: string, priority: LeadPriorityScore) => void;
    onRuleDeleted: (ruleId: string) => void;
};

// A team can only be scored by shared rules, so the board is locked to that scope: private rules stay
// out of the picture and adding a rule goes straight to the shared wizard.
export const TeamScoringBoard = ({
    filtersList,
    isBootstrapping,
    selectedRuleIds,
    defaultTeamIds,
    onToggleRule,
    onRuleDeleted,
}: Props) => {
    const { translate } = useTranslate();

    // Account-scoped rules are not attached to any team, so this board cannot assign or unassign them.
    const getSelectionDisabledReason = useCallback(
        (rule: IScoringRuleDto) =>
            isScoringRuleAccountScoped(rule.teamIds) ? translate(SELECTION_DISABLED_TOOLTIP_KEY) : undefined,
        [translate],
    );

    return (
        <DualScoringBoardContainer
            filtersList={filtersList}
            isBootstrapping={isBootstrapping}
            lockedScope={ScoringConfigType.Account}
            automationPrefix="team-scoring-board"
            selectedRuleIds={selectedRuleIds}
            pinSelectedFirst
            showUsedBadgeForSelected
            onSelect={onToggleRule}
            getSelectionDisabledReason={getSelectionDisabledReason}
            onRuleDeleted={onRuleDeleted}
            defaultTeamIds={defaultTeamIds}
        />
    );
};
